<template>
  <div>
    <Navbar />

    <main class="setup-page">
      <div class="setup-bg">
        <div class="setup-bg-overlay"></div>
      </div>

      <div class="setup-container">
        <!-- Header -->
        <div class="setup-header">
          <div class="setup-logo-badge">
            <font-awesome-icon icon="rocket" />
          </div>
          <h2 class="setup-title">Complete Your Account Setup</h2>
          <p class="setup-sub">Fill in the details below to finish setting up your account. This only takes a minute.</p>

          <!-- Step indicator -->
          <div class="step-bar">
            <div v-for="(s, i) in steps" :key="i" class="step-item" :class="{ 'step-item--active': currentStep === i, 'step-item--done': currentStep > i }">
              <div class="step-circle">
                <font-awesome-icon v-if="currentStep > i" icon="check" />
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="step-label">{{ s }}</span>
            </div>
            <div class="step-track">
              <div class="step-track-fill" :style="{ width: `${(currentStep / (steps.length - 1)) * 100}%` }"></div>
            </div>
          </div>
        </div>

        <!-- Card -->
        <div class="setup-card">

          <!-- Loading state -->
          <div v-if="infoLoading" class="info-loading">
            <font-awesome-icon icon="spinner" spin />
            <span>Loading your information…</span>
          </div>

          <!-- Setup processing overlay -->
          <div v-else-if="setupProcessing" class="processing-screen">
            <div class="proc-icon-wrap">
              <div class="proc-ring proc-ring--1"></div>
              <div class="proc-ring proc-ring--2"></div>
              <div class="proc-ring proc-ring--3"></div>
              <div class="proc-icon-inner">
                <font-awesome-icon icon="rocket" class="proc-rocket" />
              </div>
            </div>
            <h3 class="proc-title">Setting up your account</h3>
            <p class="proc-sub">Please hold on while we configure your workspace.</p>
            <div class="proc-steps">
              <div
                v-for="(msg, i) in processingMsgs"
                :key="i"
                :class="['proc-step', i < processingMsgIdx ? 'proc-step--done' : i === processingMsgIdx ? 'proc-step--active' : '']"
              >
                <span class="proc-step-icon">
                  <font-awesome-icon v-if="i < processingMsgIdx" icon="check" />
                  <font-awesome-icon v-else-if="i === processingMsgIdx" icon="spinner" spin />
                  <span v-else class="proc-step-dot"></span>
                </span>
                <span class="proc-step-text">{{ msg }}</span>
              </div>
            </div>
            <p class="proc-note">This may take up to 30 seconds — please don't close this page.</p>
          </div>

          <template v-else>

          <!-- Error box -->
          <div v-if="errorMsg" class="error-box">
            <font-awesome-icon icon="exclamation-circle" class="me-1" />
            {{ errorMsg }}
          </div>

          <!-- ── Step 0: Personal Details ─────────────────────────────── -->
          <template v-if="currentStep === 0">
            <h3 class="section-title">Personal Details</h3>
            <p class="section-sub">Confirm your name and provide additional personal information.</p>

            <!-- Email (read-only) -->
            <div class="form-group">
              <label class="form-label">Email Address</label>
              <input :value="userEmail" type="email" class="form-input form-input--readonly" readonly />
            </div>

            <div class="form-row-2">
              <div class="form-group">
                <label class="form-label">First Name <span class="req">*</span></label>
                <input v-model="form.first_name" type="text" class="form-input" placeholder="John" />
              </div>
              <div class="form-group">
                <label class="form-label">Last Name <span class="req">*</span></label>
                <input v-model="form.last_name" type="text" class="form-input" placeholder="Doe" />
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Job Title / Role</label>
              <input v-model="form.job_title" type="text" class="form-input" placeholder="e.g. CEO, Managing Director" />
            </div>

            <div class="form-row-2">
              <div class="form-group">
                <label class="form-label">Date of Birth</label>
                <input v-model="form.date_of_birth" type="date" class="form-input" />
              </div>
              <div class="form-group">
                <label class="form-label">Gender</label>
                <select v-model="form.gender" class="form-input">
                  <option value="">— Select —</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div class="step-actions">
              <button class="btn btn-primary" @click="nextStep" :disabled="!canProceedStep0">
                Next <font-awesome-icon icon="arrow-right" class="ms-1" />
              </button>
            </div>
          </template>

          <!-- ── Step 1: Address Information ─────────────────────────── -->
          <template v-if="currentStep === 1">
            <h3 class="section-title">Address Information</h3>
            <p class="section-sub">Provide your business / registered address.</p>

            <div class="form-group">
              <label class="form-label">Address Line 1 <span class="req">*</span></label>
              <input v-model="form.address_line_1" type="text" class="form-input" placeholder="123 Business Street" />
            </div>

            <div class="form-group">
              <label class="form-label">Address Line 2 <span class="optional">(optional)</span></label>
              <input v-model="form.address_line_2" type="text" class="form-input" placeholder="Suite / Floor / Building" />
            </div>

            <div class="form-row-2">
              <div class="form-group">
                <label class="form-label">City <span class="req">*</span></label>
                <input v-model="form.city" type="text" class="form-input" placeholder="Lagos" />
              </div>
              <div class="form-group">
                <label class="form-label">State / Province</label>
                <input v-model="form.state" type="text" class="form-input" placeholder="Lagos State" />
              </div>
            </div>

            <div class="form-row-2">
              <div class="form-group">
                <label class="form-label">Country <span class="req">*</span></label>
                <input v-model="form.country" type="text" class="form-input" placeholder="Nigeria" />
              </div>
              <div class="form-group">
                <label class="form-label">Postal / ZIP Code</label>
                <input v-model="form.postal_code" type="text" class="form-input" placeholder="100001" />
              </div>
            </div>

            <div class="step-actions">
              <button class="btn btn-outline" @click="currentStep--">
                <font-awesome-icon icon="arrow-left" class="me-1" /> Back
              </button>
              <button class="btn btn-primary" @click="nextStep" :disabled="!canProceedStep1">
                Next <font-awesome-icon icon="arrow-right" class="ms-1" />
              </button>
            </div>
          </template>

          <!-- ── Step 2: Module Selection ─────────────────────────────── -->
          <template v-if="currentStep === 2">
            <h3 class="section-title">Select Your Modules</h3>
            <p class="section-sub">
              Choose the modules you want to access on the platform. You can always adjust these later in your settings.
            </p>

            <!-- Select All toggle -->
            <div class="select-all-row">
              <label class="select-all-label">
                <input type="checkbox" :checked="allSelected" @change="toggleAll" class="select-all-check" />
                <span class="select-all-text">{{ allSelected ? 'Deselect All' : 'Select All Modules' }}</span>
              </label>
              <span class="module-count">{{ form.enabled_modules.length }} of {{ modules.length }} selected</span>
            </div>

            <div class="modules-grid">
              <label
                v-for="mod in modules"
                :key="mod.key"
                class="module-card"
                :class="{ 'module-card--active': form.enabled_modules.includes(mod.key) }"
              >
                <input
                  type="checkbox"
                  :value="mod.key"
                  v-model="form.enabled_modules"
                  class="module-check"
                />
                <div class="module-icon">
                  <font-awesome-icon :icon="mod.icon" />
                </div>
                <div class="module-info">
                  <span class="module-name">{{ mod.label }}</span>
                  <span class="module-desc">{{ mod.desc }}</span>
                </div>
                <div class="module-tick" v-if="form.enabled_modules.includes(mod.key)">
                  <font-awesome-icon icon="check-circle" />
                </div>
              </label>
            </div>

            <div class="step-actions">
              <button class="btn btn-outline" @click="currentStep--">
                <font-awesome-icon icon="arrow-left" class="me-1" /> Back
              </button>
              <button
                class="btn btn-primary"
                @click="submitSetup"
                :disabled="loading || form.enabled_modules.length === 0"
              >
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Saving...' : 'Complete Setup' }}
              </button>
            </div>
          </template>

          </template>
        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/footer';

const MODULES = [
  { key: 'hr_payroll',        label: 'HR & Payroll',        desc: 'Employees, payroll, leave, loans, attendance & exit management',  icon: 'users' },
  { key: 'rota_shift',        label: 'Rota & Shift',         desc: 'Shift scheduling and rota management',           icon: 'calendar-week' },
  { key: 'accounting',        label: 'Accounting',           desc: 'Journals, ledgers and financial reports',        icon: 'calculator' },
  { key: 'inventory',         label: 'Inventory',            desc: 'Products, stock transfers & adjustments',        icon: 'boxes' },
  { key: 'invoicing_billing', label: 'Invoicing & Billing',  desc: 'Client invoices and recurring billing plans',    icon: 'file-invoice' },
  { key: 'assets',            label: 'Asset Module',         desc: 'Track, allocate and manage company assets',      icon: 'landmark' },
  { key: 'procurement',       label: 'Procurement',          desc: 'Vendors, purchase orders, sourcing & GRNs',      icon: 'clipboard-list' },
  { key: 'crm',              label: 'CRM',                  desc: 'Leads, opportunities & customer engagement',     icon: 'handshake' },
  { key: 'project',         label: 'Project Module',       desc: 'Plan, staff, cost and bill client projects',      icon: 'diagram-project' },
  { key: 'visitors',        label: 'Visitor Management',   desc: 'Pre-register, check in/out and track site visitors', icon: 'id-badge' },
  { key: 'developer',       label: 'Developer API',        desc: 'API keys, webhooks and third-party integrations',   icon: 'code' },
];

export default {
  name: 'AccountSetup',
  components: { Navbar, Footer },

  setup() {
    const router    = useRouter();
    const toast     = useToast();
    const loading         = ref(false);
    const infoLoading     = ref(true);
    const setupProcessing = ref(false);
    const errorMsg        = ref('');
    const currentStep     = ref(0);
    const userEmail       = ref('');

    const processingMsgs = [
      'Creating your workspace…',
      'Setting up roles & permissions…',
      'Seeding your default data…',
      'Configuring your selected modules…',
      'Setting up approval workflows…',
      'Finalising your account…',
    ];
    const processingMsgIdx = ref(0);
    let processingTimer = null;

    const startProcessingCycle = () => {
      processingMsgIdx.value = 0;
      processingTimer = setInterval(() => {
        if (processingMsgIdx.value < processingMsgs.length - 1) {
          processingMsgIdx.value++;
        }
      }, 3500);
    };

    const stopProcessingCycle = () => {
      if (processingTimer) {
        clearInterval(processingTimer);
        processingTimer = null;
      }
    };

    const steps = ['Personal Details', 'Address', 'Modules'];
    const modules = MODULES;

    const form = ref({
      first_name:      '',
      last_name:       '',
      job_title:       '',
      date_of_birth:   '',
      gender:          '',
      address_line_1:  '',
      address_line_2:  '',
      city:            '',
      state:           '',
      country:         '',
      postal_code:     '',
      enabled_modules: [],
    });

    onMounted(async () => {
      const token = localStorage.getItem('setup_token');
      if (!token) {
        toast.error('No setup session found. Please activate your account first.');
        router.push('/auth/login');
        return;
      }

      try {
        const { data } = await axios.get('/account-setup-info', { params: { setup_token: token } });
        if (data?.data) {
          form.value.first_name = data.data.first_name || '';
          form.value.last_name  = data.data.last_name  || '';
          userEmail.value       = data.data.email      || '';
        }
      } catch (e) {
        const msg = e.response?.data?.message;
        if (msg) {
          errorMsg.value = msg;
        }
        // Non-fatal: user can still fill in the fields manually
      } finally {
        infoLoading.value = false;
      }
    });

    const canProceedStep0 = computed(() => form.value.first_name.trim() && form.value.last_name.trim());
    const canProceedStep1 = computed(() => form.value.address_line_1.trim() && form.value.city.trim() && form.value.country.trim());
    const allSelected     = computed(() => form.value.enabled_modules.length === modules.length);

    const nextStep = () => {
      errorMsg.value = '';
      currentStep.value++;
    };

    const toggleAll = () => {
      form.value.enabled_modules = allSelected.value ? [] : modules.map(m => m.key);
    };

    const submitSetup = async () => {
      if (form.value.enabled_modules.length === 0) {
        errorMsg.value = 'Please select at least one module.';
        return;
      }
      loading.value         = true;
      setupProcessing.value = true;
      errorMsg.value        = '';
      startProcessingCycle();
      const setupToken = localStorage.getItem('setup_token');
      try {
        await axios.post('/account-setup', {
          setup_token:     setupToken,
          first_name:      form.value.first_name,
          last_name:       form.value.last_name,
          job_title:       form.value.job_title,
          date_of_birth:   form.value.date_of_birth || null,
          gender:          form.value.gender,
          address_line_1:  form.value.address_line_1,
          address_line_2:  form.value.address_line_2,
          city:            form.value.city,
          state:           form.value.state,
          country:         form.value.country,
          postal_code:     form.value.postal_code,
          enabled_modules: form.value.enabled_modules,
        });
        stopProcessingCycle();
        processingMsgIdx.value = processingMsgs.length - 1;
        localStorage.removeItem('setup_token');
        await new Promise(r => setTimeout(r, 800));
        toast.success('Account setup complete! You can now log in.');
        router.push('/auth/login');
      } catch (err) {
        stopProcessingCycle();
        setupProcessing.value = false;
        errorMsg.value = err.response?.data?.message ?? 'Something went wrong. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    return {
      loading, infoLoading, setupProcessing, errorMsg, currentStep, steps, modules, form, userEmail,
      processingMsgs, processingMsgIdx,
      canProceedStep0, canProceedStep1, allSelected,
      nextStep, toggleAll, submitSetup,
    };
  },
};
</script>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────────────── */
.setup-page {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}
.setup-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: url('/static/img/banner-bg-5.jpg') center/cover no-repeat;
}
.setup-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(5,27,53,.97) 0%, rgba(0,74,173,.85) 100%);
}
.setup-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 720px;
  padding: 40px 24px 60px;
}

/* ── Header ──────────────────────────────────────────────────────────── */
.setup-header { text-align: center; margin-bottom: 28px; }
.setup-logo-badge {
  width: 56px; height: 56px;
  background: rgba(255,255,255,.15);
  border-radius: 16px;
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; font-size: 24px;
  margin-bottom: 14px;
}
.setup-title { color: #fff; font-size: 26px; font-weight: 800; margin: 0 0 6px; }
.setup-sub   { color: rgba(255,255,255,.7); font-size: 14px; margin: 0 0 24px; }

/* ── Step bar ────────────────────────────────────────────────────────── */
.step-bar {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  position: relative;
  padding: 0 16px;
}
.step-track {
  position: absolute;
  top: 18px;
  left: calc(50% - 110px);
  width: 220px;
  height: 2px;
  background: rgba(255,255,255,.2);
  z-index: 0;
}
.step-track-fill { height: 100%; background: #fff; transition: width .35s ease; }

.step-item {
  display: flex; flex-direction: column; align-items: center;
  gap: 6px; flex: 1; z-index: 1;
}
.step-circle {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,.2);
  color: rgba(255,255,255,.6);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700;
  border: 2px solid rgba(255,255,255,.3);
  transition: background .25s, border-color .25s, color .25s;
}
.step-item--active .step-circle { background: #fff; color: #004AAD; border-color: #fff; }
.step-item--done   .step-circle { background: #22c55e; color: #fff; border-color: #22c55e; }
.step-label {
  font-size: 11px; font-weight: 600;
  color: rgba(255,255,255,.55);
  text-transform: uppercase; letter-spacing: .5px;
  white-space: nowrap;
}
.step-item--active .step-label,
.step-item--done   .step-label { color: #fff; }

/* ── Card ────────────────────────────────────────────────────────────── */
.setup-card {
  background: #fff;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 24px 60px rgba(0,0,0,.22);
}

/* ── Info loading ────────────────────────────────────────────────────── */
.info-loading {
  display: flex; align-items: center; gap: 10px;
  color: #7a90b0; font-size: 14px; padding: 16px 0;
}

/* ── Error box ───────────────────────────────────────────────────────── */
.error-box {
  background: rgba(224,53,53,.07);
  border: 1px solid rgba(224,53,53,.3);
  color: #c0392b;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  margin-bottom: 18px;
  display: flex; align-items: flex-start; gap: 8px;
}

/* ── Section headings ────────────────────────────────────────────────── */
.section-title { font-size: 17px; font-weight: 700; color: #1a2b45; margin: 0 0 4px; }
.section-sub   { font-size: 13px; color: #7a90b0; margin: 0 0 22px; }

/* ── Form ────────────────────────────────────────────────────────────── */
.form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.form-label { font-size: 13px; font-weight: 600; color: #3d5068; }
.req      { color: #e03535; }
.optional { color: #a0b0c4; font-weight: 400; font-size: 12px; }

.form-input {
  padding: 10px 12px;
  border: 1.5px solid #dde5f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1a2b45;
  background: #fff;
  transition: border-color .15s;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}
.form-input:focus { border-color: #004AAD; }
.form-input--readonly {
  background: #f8fafc;
  color: #7a90b0;
  cursor: not-allowed;
}

/* ── Module grid ─────────────────────────────────────────────────────── */
.select-all-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
}
.select-all-label { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.select-all-check { width: 16px; height: 16px; accent-color: #004AAD; cursor: pointer; }
.select-all-text  { font-size: 14px; font-weight: 600; color: #3d5068; }
.module-count     { font-size: 12px; color: #7a90b0; font-weight: 500; }

.modules-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 6px; }

.module-card {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  border: 1.5px solid #dde5f0;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color .15s, background .15s;
  position: relative;
}
.module-card:hover { border-color: #004AAD; background: rgba(0,74,173,.03); }
.module-card--active { border-color: #004AAD; background: rgba(0,74,173,.05); }
.module-check { display: none; }

.module-icon {
  width: 36px; height: 36px; border-radius: 9px;
  background: rgba(0,74,173,.08);
  color: #004AAD;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; flex-shrink: 0;
}
.module-card--active .module-icon { background: #004AAD; color: #fff; }

.module-info { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.module-name { font-size: 13px; font-weight: 700; color: #1a2b45; }
.module-desc { font-size: 11px; color: #7a90b0; line-height: 1.4; }

.module-tick { color: #004AAD; font-size: 16px; flex-shrink: 0; }

/* ── Step actions ────────────────────────────────────────────────────── */
.step-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }
.btn {
  padding: 11px 24px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: background .15s, transform .1s;
  display: inline-flex; align-items: center; gap: 6px;
}
.btn:disabled { opacity: .55; cursor: not-allowed; }
.btn:active:not(:disabled) { transform: scale(.98); }

.btn-primary { background: #004AAD; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #003b8e; }

.btn-outline { background: transparent; color: #3d5068; border: 1.5px solid #dde5f0; }
.btn-outline:hover:not(:disabled) { background: #f6f9ff; }

/* ── Processing screen ───────────────────────────────────────────────── */
.processing-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 36px;
  text-align: center;
}

/* Animated rings + rocket */
.proc-icon-wrap {
  position: relative;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
}
.proc-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid transparent;
  animation: proc-spin linear infinite;
}
.proc-ring--1 {
  border-top-color: #004AAD;
  border-right-color: rgba(0,74,173,.2);
  border-bottom-color: rgba(0,74,173,.2);
  border-left-color: rgba(0,74,173,.2);
  animation-duration: 1.4s;
}
.proc-ring--2 {
  inset: 10px;
  border-top-color: transparent;
  border-right-color: #22c55e;
  border-bottom-color: rgba(34,197,94,.2);
  border-left-color: rgba(34,197,94,.2);
  animation-duration: 1.9s;
  animation-direction: reverse;
}
.proc-ring--3 {
  inset: 20px;
  border-top-color: rgba(251,191,36,.6);
  border-right-color: transparent;
  border-bottom-color: transparent;
  border-left-color: rgba(251,191,36,.3);
  animation-duration: 2.6s;
}
@keyframes proc-spin {
  to { transform: rotate(360deg); }
}
.proc-icon-inner {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #004AAD, #0066cc);
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0,74,173,.35);
}
.proc-rocket {
  color: #fff;
  font-size: 20px;
  animation: proc-bounce .8s ease-in-out infinite alternate;
}
@keyframes proc-bounce {
  from { transform: translateY(2px); }
  to   { transform: translateY(-2px); }
}

.proc-title {
  font-size: 20px;
  font-weight: 800;
  color: #1a2b45;
  margin: 0 0 6px;
}
.proc-sub {
  font-size: 14px;
  color: #7a90b0;
  margin: 0 0 28px;
}

/* Step list */
.proc-steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 360px;
  margin-bottom: 28px;
  text-align: left;
}
.proc-step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 500;
  color: #b0bec5;
  background: #f8fafc;
  border: 1.5px solid #f0f4f8;
  transition: all .3s ease;
}
.proc-step--active {
  color: #004AAD;
  background: #eff6ff;
  border-color: #bfdbfe;
  font-weight: 600;
}
.proc-step--done {
  color: #15803d;
  background: #f0fdf4;
  border-color: #86efac;
}
.proc-step-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}
.proc-step--done .proc-step-icon { color: #22c55e; }
.proc-step--active .proc-step-icon { color: #004AAD; }
.proc-step-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #d1d5db;
  border-radius: 50%;
}
.proc-step-text { flex: 1; }

.proc-note {
  font-size: 12px;
  color: #a0b0c4;
  margin: 0;
  font-style: italic;
}

.me-1 { margin-right: 4px; }
.ms-1 { margin-left: 4px; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .setup-card      { padding: 24px 16px; }
  .form-row-2      { grid-template-columns: 1fr; }
  .modules-grid    { grid-template-columns: 1fr; }
  .step-track      { left: 16px; width: calc(100% - 32px); }
}
</style>
